/**
 * Which template the prototype on screen belongs to, kept current as its files change (US 359).
 *
 * One answer for everything that depends on it - the device the preview is framed in, and the
 * vendor bundle the light preview loads - so the two cannot disagree. They used to: the frame
 * came from a picker and the bundle from `package.json`, which the store never holds, so a
 * mobile prototype could sit in a phone frame running the web bundle.
 *
 * Reactive on purpose. A new prototype opens with no files and turns mobile only when the build
 * writes the seeded Framework7 shell, so a value read once when the workspace opened would stay
 * `web` for the whole build.
 */

import { useStore } from '@nanostores/react';
import { useMemo } from 'react';
import { workbenchStore } from '../stores/workbench';
import { templateIdForFiles, type TemplateId } from './headlessTemplate';

export function usePrototypeTemplate(): TemplateId {
  const files = useStore(workbenchStore.files);
  return useMemo(() => {
    const plain: Record<string, string> = {};
    for (const [path, entry] of Object.entries(files)) {
      if (entry && typeof entry.content === 'string') plain[path] = entry.content;
    }
    return templateIdForFiles(plain);
  }, [files]);
}

/** The device a prototype of this template is previewed in. */
export function deviceIdForTemplate(template: TemplateId): string {
  return template === 'mobile' ? 'iphone-17' : 'desktop';
}
